/**
 * Remaining (sellable) slabs in a lot. `balance` is decremented by the backend on each sale;
 * `lotNos` is the original seller-entered count and never changes. Legacy lots without a
 * balance fall back to lotNos.
 */
export const lotBalance = (lot) => {
  const b = parseInt(lot?.balance);
  return Number.isNaN(b) ? (parseInt(lot?.lotNos) || 0) : b;
};

/**
 * Given minimumOrderQuantity (SFT) and a list of available lots, calculate the
 * minimum integer number of slabs needed to satisfy the SFT requirement, using a
 * sequential lot-consumption algorithm.
 *
 * Returns { minSlabs: int, selectedSft: float }
 */
export const calculateMinSlabsFromMoq = (moqSft, lots) => {
  if (!lots || lots.length === 0 || !moqSft || moqSft <= 0) return { minSlabs: 1, selectedSft: 0 };
  let remainingSft = moqSft;
  let totalSlabs = 0;
  let totalSftSelected = 0;
  for (const lot of lots) {
    if (remainingSft <= 0) break;
    const l = parseFloat(lot.lotLength) || 0;
    const w = parseFloat(lot.lotWidth) || 0;
    const availableNos = lotBalance(lot);
    if (l <= 0 || w <= 0 || availableNos <= 0) continue;
    const slabSft = (l * w) / 144;
    if (slabSft <= 0) continue;
    const requiredSlabs = Math.ceil(remainingSft / slabSft);
    const slabsFromLot = Math.min(requiredSlabs, availableNos);
    const sftFromLot = slabsFromLot * slabSft;
    totalSlabs += slabsFromLot;
    totalSftSelected += sftFromLot;
    remainingSft -= sftFromLot;
  }
  return { minSlabs: Math.max(1, totalSlabs), selectedSft: totalSftSelected };
};


/**
 * Lots of a product, falling back to the legacy flat lot fields.
 */
export const getProductLots = (product) => {
  if (!product) return [];
  if (product.lots && product.lots.length > 0) return product.lots;
  if (product.lotLength || product.lotNos) {
    return [{ lotLength: product.lotLength, lotWidth: product.lotWidth, lotNos: product.lotNos }];
  }
  return [];
};

/**
 * SFT covered by `qty` slabs, consuming lots sequentially (same as the product detail page).
 */
export const calculateSftForSlabs = (lots, qty) => {
  if (!lots || lots.length === 0 || !qty || qty <= 0) return 0;
  let remaining = qty;
  let sft = 0;
  for (const lot of lots) {
    if (remaining <= 0) break;
    const nos = lotBalance(lot);
    const l = parseFloat(lot.lotLength) || 0;
    const w = parseFloat(lot.lotWidth) || 0;
    if (nos <= 0 || l <= 0 || w <= 0) continue;
    const consumed = Math.min(nos, remaining);
    sft += (consumed * l * w) / 144;
    remaining -= consumed;
  }
  return sft;
};

/**
 * Minimum number of slabs a customer must order for a product, derived from its
 * minimumOrderQuantity (SFT). Uses product.lots, falling back to the legacy flat lot fields.
 */
export const getMinOrderSlabs = (product) => {
  if (!product) return 1;
  const lots = product.lots && product.lots.length > 0
    ? product.lots
    : (product.lotLength || product.lotNos)
      ? [{ lotLength: product.lotLength, lotWidth: product.lotWidth, lotNos: product.lotNos }]
      : [];
  const moqSft = parseFloat(product.minimumOrderQuantity) || 0;
  if (moqSft <= 0 || lots.length === 0) return 1;
  return calculateMinSlabsFromMoq(moqSft, lots).minSlabs;
};
